import { DEFAULT_SPACING_CONFIG } from '../context/node-generation-context';
import LayoutEngine from '../layout/auto-layout-engine';
import applyTwoPhaseLayout from '../layout/layout-helpers/applyTwoPhaseLayout';
import WorkflowParser from '../layout/workflow-parser';

export const generateNodesAndEdgesV2 = (
  recipe,
  config = {
    horizontalScale: DEFAULT_SPACING_CONFIG.horizontalScale,
    verticalScale: DEFAULT_SPACING_CONFIG.verticalScale,
    layerDistance: DEFAULT_SPACING_CONFIG.layerDistance,
    nodeDistance: DEFAULT_SPACING_CONFIG.nodeDistance,
    edgeType: DEFAULT_SPACING_CONFIG.edgeType,
    showActionFrames: DEFAULT_SPACING_CONFIG.showActionFrames,
    layoutType: DEFAULT_SPACING_CONFIG.layoutType,
  },
  alert
) => {
  const {
    showActionFrames,
    layoutType,
    horizontalScale,
    verticalScale,
    layerDistance,
    nodeDistance,
    edgeType,
  } = config;
  const spacingConfig = {
    horizontalScale,
    verticalScale,
    layerDistance,
    nodeDistance,
  };
  const currentLayoutType = layoutType;
  const workflowParser = new WorkflowParser(alert);
  const {
    nodes: parsedNodes,
    edges: parsedEdges,
    actionFrames,
  } = workflowParser.parseWorkflow({ recipe }, showActionFrames);

  // Enhance nodes with path depth information for layout
  const enhancedNodes = parsedNodes.map((node) => {
    // For action frames, inherit path depth from the frame's root node
    if (node.type === 'actionFrame') {
      const frameId = node.id;
      const frame = actionFrames[frameId];
      const rootNodeId = frame?.rootNode;
      const rootNode = parsedNodes.find((n) => n.id === rootNodeId);

      if (rootNode) {
        return {
          ...node,
          pathDepth: rootNode.data?.pathDepth || 0,
          data: {
            ...node.data,
            pathDepth: rootNode.data?.pathDepth || 0,
          },
          position: node.position || { x: 0, y: 0 },
          // Don't hardcode dimensions - let layout engine handle this dynamically
        };
      }
    }

    // For regular nodes, add default dimensions for layout calculation
    return {
      ...node,
      position: node.position || { x: 0, y: 0 },
      // Provide default dimensions for layout calculation only
      _width: node.width || node.style?.width || 250,
      _height: node.height || node.style?.height || 100,
    };
  });

  // Apply the two-phase layout approach
  const layoutedNodes = applyTwoPhaseLayout(enhancedNodes, parsedEdges, {
    layoutType: currentLayoutType,
    layerDistance: spacingConfig.layerDistance,
    nodeDistance: spacingConfig.nodeDistance,
    horizontalScale: spacingConfig.horizontalScale,
    verticalScale: spacingConfig.verticalScale,
  });

  // Sort nodes to ensure parent nodes come before children (CRITICAL for ReactFlow)
  layoutedNodes.sort((a, b) => {
    // If node B is a child of node A, A should come first
    if (b.parentNode === a.id) return -1;
    // If node A is a child of node B, B should come first
    if (a.parentNode === b.id) return 1;
    // Otherwise keep original order
    return 0;
  });

  // Verify node positions are valid (not NaN) and ensure parent-child relationships are preserved
  const validatedNodes = layoutedNodes.map((node) => {
    const position = {
      x: isNaN(node.position?.x) ? 100 : node.position.x,
      y: isNaN(node.position?.y) ? 100 : node.position.y,
    };

    // Process action frames with explicit dimensions and zIndex
    if (node.type === 'actionFrame') {
      return {
        ...node,
        position,
        // Preserve dynamically calculated dimensions from layout engine
        style: {
          ...node.style,
          zIndex: -1,
          background: 'rgba(240, 240, 240, 0.25)',
          border: '1px solid #ddd',
        },
        zIndex: -1, // Ensure frame is behind other nodes
      };
    }

    // For regular nodes, preserve parent-child relationship
    const result = {
      ...node,
      position,
    };

    // Ensure parentNode and extent properties are preserved
    if (node.parentNode) {
      result.parentNode = node.parentNode;
      result.parentId = node.parentNode; // Add parentId for compatibility with newer ReactFlow versions
      result.extent = 'parent';
      result.zIndex = 1; // Ensure child nodes are in front of frames
      result.expandParent = true; // Make sure child expands parent if needed
    }

    return result;
  });

  // Apply better edge routing
  const enhancedEdges = LayoutEngine.routeEdgesWithActionFrames(
    parsedEdges,
    validatedNodes,
    actionFrames
  );

  // Preserve edge type in the enhanced edges
  const edgesWithPreservedType = enhancedEdges.map((edge) => ({
    ...edge,
    type: edgeType, // Preserve the current edge type
  }));

  return {
    // nodes: repositionNodesToAvoidOverlaps(validatedNodes),
    nodes: validatedNodes,
    edges: edgesWithPreservedType,
    actionFrames,
  };
};
